import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, FormControl, Alert } from 'react-bootstrap';
import useLocalization from '../../../../../hooks/useLocalization';
import HorizontalProgress from '../../../../common/HorizontalProgress';
import { ExclamationTriangle } from 'react-bootstrap-icons';

const StrategySandboxTransferModal = ({
  show,
  allStrategies = [],
  strategy,
  strategies: strategiesProp,
  onHide,
  showProgress,
  onSubmit
}) => {
  const { translate } = useLocalization();
  const [nameOverrides, setNameOverrides] = useState({});


  const strategies = useMemo(() => {
    if (strategiesProp?.length) return strategiesProp;
    if (strategy) return [strategy];
    return [];
  }, [strategiesProp, strategy]);

  const isBulk = strategies.length > 1;
  const primaryStrategy = strategies[0];

  const sandboxedStrategies = useMemo(() => allStrategies.filter(s => s.isSandboxed), [allStrategies]);
  const mainStrategies = useMemo(() => allStrategies.filter(s => !s.isSandboxed), [allStrategies]);

  const targetNames = useMemo(() => {
    if (!primaryStrategy) return [];
    return (primaryStrategy.isSandboxed ? mainStrategies : sandboxedStrategies).map(s => s.name);
  }, [primaryStrategy, sandboxedStrategies, mainStrategies]);

  const conflictingStrategies = useMemo(
    () => strategies.filter(s => targetNames.includes(s.name)),
    [strategies, targetNames]
  );

  useEffect(() => {
    const overrides = {};
    strategies.forEach(s => {
      overrides[s._id] = s.name || '';
    });
    setNameOverrides(overrides);
  }, [strategies]);

  const confirmMessageKey = primaryStrategy?.isSandboxed
    ? isBulk
      ? 'are_you_sure_to_move_these_strategies_to_mainbox'
      : 'are_you_sure_to_move_this_strategy_to_mainbox'
    : isBulk
    ? 'are_you_sure_to_move_these_strategies_to_sandbox'
    : 'are_you_sure_to_move_this_strategy_to_sandbox';

  const handleSubmit = () => {
    const payload = strategies.map(s => ({
      strategy: s,
      newName: conflictingStrategies.some(c => c._id === s._id)
        ? (nameOverrides[s._id] || '').trim()
        : s.name
    }));

    // Keep single-strategy callers on the original string API
    onSubmit(isBulk ? payload : payload[0]?.newName);
  };

  return (
    <Modal className="bg-dark bg-opacity-25" show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton={!showProgress}>
        <Modal.Title>
          <h6 className="mb-0">
            {translate('strategy_transfer')} ({primaryStrategy?.name}
            {isBulk ? ` +${strategies.length - 1}` : ''})
          </h6>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="overflow-auto p-4">
        {primaryStrategy && (
          <div className="text-center">
            <ExclamationTriangle className="text-danger" size={30} />
            <h6 className="mt-3">{translate(confirmMessageKey)}</h6>

            {isBulk && (
              <ul className="text-start mt-3 mb-0 small">
                {strategies.map(s => (
                  <li key={s._id}>{s.name}</li>
                ))}
              </ul>
            )}

            {conflictingStrategies.length > 0 && (
              <Alert variant="danger" className="p-1 text-start mt-4">
                <p className="mt-2">{translate('strategy_name_exists')}</p>
                {conflictingStrategies.map(s => (
                  <div key={s._id} className="mt-2">
                    {isBulk && <label className="small text-muted mb-1 d-block">{s.name}</label>}
                    <FormControl
                      placeholder={translate('enter_new_strategy_name')}
                      value={nameOverrides[s._id] || ''}
                      onChange={e => setNameOverrides(prev => ({ ...prev, [s._id]: e.target.value }))}
                      className="mt-2"
                    />
                  </div>
                ))}
              </Alert>
            )}
          </div>
        )}

        {showProgress && <HorizontalProgress text={translate('please_wait')} />}
      </Modal.Body>
      <Modal.Footer>
        <Button
          disabled={showProgress}
          size="sm"
          variant="success"
          className="mr-2 text-white"
          onClick={handleSubmit}
        >
          {translate('move')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StrategySandboxTransferModal;
